/**
 * @file ConsentEditTabs.tsx
 * @description Вкладки формы новой редакции и поля выбранной вкладки
 * (phase19.16, Р3г; uiux-r3g-consent-texts §3): «Баннер • / Окно «Настроить»
 * / Кнопки и подвал / Окно о риске», точка «•» — на вкладке есть ошибка.
 * Вкладка другого вида (окно о риске нумеруется отдельно) переключает вид
 * через адрес — несохранённые правки ловит `ConsentLeaveGuard`.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { CONSENT_TEXT_TABS, consentTabOf, type ConsentTextsTab } from "../../../lib/platform/consent-texts-fields";
import { Tabs } from "../../ui/Tabs";
import { ConsentTextsFieldList } from "./ConsentTextsFieldList";

interface Props {
  kind: ConsentTextKind;
  tab: ConsentTextsTab;
  onTab: (tab: ConsentTextsTab) => void;
  texts: object;
  onChange: (path: string, value: string) => void;
  errors: ReadonlyMap<string, string>;
  warnings: ReadonlyMap<string, string>;
  highlighted: ReadonlySet<string>;
  baseNumber: number;
  placeholderHint?: ReactNode;
}

const ERROR_MARK = "есть ошибка";

export function ConsentEditTabs({ kind, tab, onTab, texts, onChange, errors, warnings, highlighted, baseNumber, placeholderHint }: Props) {
  const withErrors = new Set([...errors.keys()].map((path) => consentTabOf(kind, path)));
  const items = CONSENT_TEXT_TABS.map((t) => ({
    value: t.value,
    label: t.label,
    marker: t.kind === kind && withErrors.has(t.value) ? ERROR_MARK : undefined,
  }));

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 shrink-0 flex-col gap-1">
        <Tabs aria-label="Разделы текстов" items={items} value={tab} onChange={onTab} />
        {withErrors.size > 0 ? (
          <p className="type-caption m-0 flex items-center gap-2 text-on-surface-variant">
            <span aria-hidden className="inline-block size-2 rounded-full bg-primary" />— на вкладке есть ошибка
          </p>
        ) : null}
      </div>
      <div role="tabpanel" aria-label={CONSENT_TEXT_TABS.find((t) => t.value === tab)?.label}>
        <ConsentTextsFieldList
          tab={tab}
          kind={kind}
          texts={texts}
          onChange={onChange}
          errors={errors}
          warnings={warnings}
          highlighted={highlighted}
          baseNumber={baseNumber}
          placeholderHint={placeholderHint}
        />
      </div>
    </div>
  );
}
